import "./InvoiceList.scss";

import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { BiDownload } from "react-icons/bi";
import { FiCheck, FiSend } from "react-icons/fi";

import Button from "../../../components/Utils/Button/Button";
import DynamicTable from "../../../components/Utils/DynamicTable/DynamicTable";
import Spinner from "../../../components/Utils/Spinner/Spinner";
import { StyledInputText } from "../../../components/Utils/StyledInput/StyledInput";
import StyledSwitch from "../../../components/Utils/StyledSwitch/StyledSwitch";
import useLoadingStore from "../../../stores/LoadingStore";
import InvoiceType from "../../../types/Invoice";
import appFetch from "../../../Utils/Services/Fetch/appFetch";

const InvoiceList = () => {
  const [invoices, setInvoices] = useState<InvoiceType[]>();
  const [search, setSearch] = useState("");
  const { loadingStates, setLoading } = useLoadingStore();
  const [showValidated, setShowValidated] = useState(
    localStorage.getItem("showValidated") === "true",
  );
  const [sortState, setSortState] = useState<{
    column: string;
    direction: "asc" | "desc";
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading("fetchInvoices", true);
      const invoiceData = await appFetch(`/api/invoice/getInvoiceList`);
      if (invoiceData) {
        setInvoices(invoiceData.invoices);
      }
      setLoading("fetchInvoices", false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Save the showValidated state to localStorage
    localStorage.setItem("showValidated", showValidated.toString());
  }, [showValidated]);

  const handleSort = (
    column:
      | keyof InvoiceType
      | "client.client_name"
      | "quote.discount"
      | "type",
  ) => {
    const direction = sortState?.direction === "asc" ? "desc" : "asc";
    setSortState({ column, direction });
    setInvoices((prevInvoices) => {
      if (!prevInvoices) return prevInvoices;
      return prevInvoices.slice().sort((a, b) => {
        let aValue: any;
        let bValue: any;
        if (column === "client.client_name") {
          aValue = a.client?.client_name;
          bValue = b.client?.client_name;
        } else if (column === "quote.discount") {
          aValue = Number(a.quote?.discount);
          bValue = Number(b.quote?.discount);
        } else if (column === "total_amount") {
          aValue = Number(a.total_amount);
          bValue = Number(b.total_amount);
        } else if (column === "type") {
          aValue = a.quote_id ? "Location" : "Vente";
          bValue = b.quote_id ? "Location" : "Vente";
        } else {
          aValue = a[column];
          bValue = b[column];
        }
        if (aValue == null || bValue == null) return 0;
        if (aValue < bValue) return direction === "asc" ? -1 : 1;
        if (aValue > bValue) return direction === "asc" ? 1 : -1;
        return 0;
      });
    });
  };

  const filteredInvoices = invoices?.filter((invoice) => {
    const matchesSearch =
      invoice.client?.client_name
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      invoice.invoice_id.toString().includes(search.toLowerCase());

    const matchesValidatedFilter =
      showValidated || invoice.status !== "Validated";

    return matchesSearch && matchesValidatedFilter;
  });

  const downloadPDF = async (invoice: InvoiceType) => {
    setLoading(`downloadInvoice${invoice.invoice_id}`, true);
    const response = await appFetch(
      `/api/invoice/getInvoicePdf?invoice_id=${invoice.invoice_id}`,
    );

    if (response && response instanceof Blob) {
      const filename = `FACTURE-${invoice.formatted_invoice_id}.pdf`;

      const blobURL = URL.createObjectURL(response);
      const link = document.createElement("a");
      link.href = blobURL;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (response && response.error) {
      toast.error(response.message);
    }
    setLoading(`downloadInvoice${invoice.invoice_id}`, false);
  };

  const validateInvoice = async (invoiceId: number) => {
    setLoading(`validateInvoice${invoiceId}`, true);
    const response = await appFetch(
      `/api/invoice/validateInvoice?invoice_id=${invoiceId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response && response.error) {
      toast.error(response.message);
    } else if (response && response.message) {
      toast.success(response.message);
      const invoiceData = await appFetch("/api/invoice/getInvoiceList");
      if (invoiceData) {
        setInvoices(invoiceData.invoices);
      }
    }
    setLoading(`validateInvoice${invoiceId}`, false);
  };

  const sendInvoice = async (invoice: InvoiceType) => {
    setLoading(`sendInvoice${invoice.invoice_id}`, true);
    try {
      const response = await appFetch(
        `/api/invoice/sendInvoice?invoice_id=${invoice.invoice_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response && response.success) {
        toast.success(response.message || "Facture envoyée avec succès !");
      } else if (response && response.error) {
        toast.error(response.message || "Erreur lors de l'envoi de la facture");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi de la facture");
    }
    setLoading(`sendInvoice${invoice.invoice_id}`, false);
  };

  const collumns = [
    {
      title: "ID",
      dataIndex: "formatted_invoice_id",
      render: (_formatted_invoice_id: string, invoice: InvoiceType) => (
        <span>{invoice.formatted_invoice_id}</span>
      ),
      sortable: true,
      onSort: () => handleSort("formatted_invoice_id"),
      sortableClass:
        sortState?.column === "formatted_invoice_id"
          ? sortState.direction
          : undefined,
    },
    {
      title: "Type",
      dataIndex: "quote_id",
      render: (_quote_id: number, invoice: InvoiceType) =>
        invoice.quote_id ? "Location" : "Vente",
      sortable: true,
      onSort: () => handleSort("type"),
      sortableClass:
        sortState?.column === "type" ? sortState.direction : undefined,
    },
    {
      title: "Client",
      dataIndex: "client.client_name",
      render: (_client_name: string, invoice: InvoiceType) =>
        invoice.client?.client_name ?? "",
      sortable: true,
      onSort: () => handleSort("client.client_name"),
      sortableClass:
        sortState?.column === "client.client_name"
          ? sortState.direction
          : undefined,
    },
    {
      title: "Date",
      dataIndex: "issue_date",
      render: (_issue_date: Date, invoice: InvoiceType) =>
        new Date(invoice.issue_date).toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
        }),
      sortable: true,
      onSort: () => handleSort("issue_date"),
      sortableClass:
        sortState?.column === "issue_date" ? sortState.direction : undefined,
    },
    {
      title: "Réduction (€)",
      dataIndex: "quote.discount",
      render: (_discount: number, invoice: InvoiceType) => (
        <span>{invoice.quote?.discount} €</span>
      ),
      sortable: true,
      onSort: () => handleSort("quote.discount"),
      sortableClass:
        sortState?.column === "quote.discount"
          ? sortState.direction
          : undefined,
    },
    {
      title: "Prix total",
      dataIndex: "total_amount",
      render: (_total_amount: number, invoice: InvoiceType) => (
        <span>{invoice.total_amount} €</span>
      ),
      sortable: true,
      onSort: () => handleSort("total_amount"),
      sortableClass:
        sortState?.column === "total_amount" ? sortState.direction : undefined,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_status: string, invoice: InvoiceType) => (
        <span>{invoice.status}</span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: string, invoice: InvoiceType) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <Button
            className="downloadButton"
            onClick={() => downloadPDF(invoice)}
            icon={<BiDownload />}
            isLoading={loadingStates[`downloadInvoice${invoice.invoice_id}`]}
            style="square"
          />

          <Button
            className="sendInvoiceButton"
            onClick={() => sendInvoice(invoice)}
            icon={<FiSend />}
            isLoading={loadingStates[`sendInvoice${invoice.invoice_id}`]}
            style="square"
          />

          {invoice.status === "Pending" && (
            <Button
              className="validateButton"
              onClick={() => validateInvoice(invoice.invoice_id)}
              icon={<FiCheck />}
              isLoading={loadingStates[`validateInvoice${invoice.invoice_id}`]}
              style="square"
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={`invoiceListPage`}>
      <div className="header">
        <StyledInputText
          label="Rechercher"
          value={search}
          setValue={setSearch}
          className="searchInput"
        />
        <StyledSwitch
          checked={showValidated}
          onChange={() => setShowValidated(!showValidated)}
          label="Afficher les factures validées"
        />
      </div>
      <div className="tableContainer">
        {loadingStates.fetchInvoices ? (
          <Spinner />
        ) : (
          <DynamicTable
            data={filteredInvoices ?? []}
            columns={collumns}
            onClick={(record) => console.log(record)}
            pagined
            maxRow={25}
            mobile
          />
        )}
      </div>
    </div>
  );
};

export default InvoiceList;
